import _ from 'lodash';
import moment from 'moment';

//import firebase from 'firebase';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/storage';
import 'firebase/firestore';
import 'firebase/functions';

//performance monitoring
import 'firebase/performance';

import { detect } from 'detect-browser';

import * as Sentry from '@sentry/browser';
import passwordGenerator from 'password-generator';

import { FullStoryAPI } from 'react-fullstory';

import APP_CONFIG from '../config';

import store from '../store';

import {
  DEFAULT_SCORED_DIMENSIONS,
  getLanguageOptions,
} from './data';

import {
  competencies
} from '../data/competencies';

import {
  dimensions,
  dimensionFamilies
} from '../data/dimensions';

const CONFIG = APP_CONFIG;

//export util files
export * from './localizers';
export * from './dealScoring';
export * from './icons';

export function appConfig(){
  return CONFIG;
}

export function getLocaleFromBrowser(){
  const preferredLanguage = navigator.language.split('-')[0];
  const allLanguageOptions = _.map(getLanguageOptions(), 'value');
  if(allLanguageOptions.indexOf(preferredLanguage) !== -1){
    return preferredLanguage;
  }
  else{
    return 'en';
  }
}

export function verifyWhitelistedEmailDomain(email, validDomains){
  let emailDomain = email.split('@')[1];

  if(validDomains.indexOf(emailDomain) !== -1){
    return true;
  }
}

export function navPrefix(){
  const prefixMatch = document.location.pathname.match(/\/people\/[^/]+/g) || '';

  return prefixMatch;
}
window.navPrefix = navPrefix;

export function isProduction(){
  return CONFIG.ENV === 'production';
}
export function isLocalhost(){
  return document.location.href.indexOf('localhost') > 0;
}

export function isDemo(){
  return CONFIG.ENV === 'demo';
}

export function isDevelopment(){
  return CONFIG.ENV === 'development';
}

export function isExport(){
  return CONFIG.ENV === 'export';
}

export function emulatingDevelopment(){
  return window.location.hash.indexOf('AIQQA') !== -1;
}

export function isAssessmentRoute(route){
  return route.indexOf('/a/') !== -1 || route === '/a';
}

export async function currentUserToken(){
  const token = await firebase.auth().currentUser.getIdToken();
  return token;
}

export function excludeTestAssessments(assessments){
  return _.filter(assessments, ({ assessmentConfig }) => {
    return !assessmentConfig.test;
  });
}

export function dealLastProgressedBy(deal, user:string = 'any'){

  // console.log('dealLastProgressedBy: ', deal);

  const combinedActivity =  _.union(_.map(deal.funnelTasks || {}, task => { return { updatedBy: task.updatedBy, updatedOn: task.updatedOn } }), _.map(deal.imperativeTasks || {}, task => { return { updatedBy: task.updatedBy, updatedOn: task.updatedOn } }));

  const activityWithDates = _.filter(combinedActivity, activityItem => {
    return activityItem.updatedOn;
  });

  const sortedActivity = _.orderBy(activityWithDates, 'updatedOn', 'desc');

  // console.log('sortedActivity: ', sortedActivity);

  if(user === 'any'){
    const latestActivity = _.head(sortedActivity);

    return latestActivity ? latestActivity.updatedOn : null;
  }
  else{
    const sortedActivityForUser = _.filter(sortedActivity, { updatedBy: user })

    const latestActivityForUser = _.head(sortedActivityForUser);

    return latestActivityForUser ? latestActivityForUser.updatedOn : null;
  }
}

export function personalizeString(stringToPersonalize, sellerName: null, gender: null){

  let replaceWith = {
    __SELLERNAME__: sellerName,
    __SELLERNAMEP__: `${sellerName}\'s`,
  };

  if(gender === 'm' || gender === 'male'){
    replaceWith = {
      ...replaceWith,
      __HESHE__: 'he',
      __HIMHER__: 'him',
      __HISHER__: 'his',
      __HIMSELFHERSELF__: 'himself',

      __HESHEC__: 'He',
      __HIMHERC__: 'Him',
      __HISHERC__: 'His',
      __HIMSELFHERSELFC__: 'Himself',
    }
  }
  else if(gender === 'f' || gender === 'female'){
    replaceWith = {
      ...replaceWith,
      __HESHE__: 'she',
      __HIMHER__: 'her',
      __HISHER__: 'her',
      __HIMSELFHERSELF__: 'herself',
      __HESHEC__: 'She',
      __HIMHERC__: 'Her',
      __HISHERC__: 'Her',
      __HIMSELFHERSELFC__: 'Herself',
    }
  }
  else{
    replaceWith = {
      ...replaceWith,
      __HESHE__: 'he or she',
      __HIMHER__: 'him or her',
      __HISHER__: 'his or her',
      __HIMSELFHERSELF__: 'himself or herself',
      __HESHEC__: 'He or she',
      __HIMHERC__: 'Him or her',
      __HISHERC__: 'His or her',
      __HIMSELFHERSELFC__: 'Himself or herself',
    }
  }

  let personalizedString = stringToPersonalize;

  _.each(Object.keys(replaceWith), replacePart => {
    personalizedString = personalizedString.replace(new RegExp(`${replacePart}`, "g"), replaceWith[replacePart]);
  });

  return personalizedString;
}
window.personalizeString = personalizeString;

export function getTaskUpdateForStatus(newStatus){

  const userId = store.getState().user.uid;

  return {
    status: newStatus,
    updatedBy: userId,
    updatedOn: moment().valueOf()
  }
}

export function dealValueInUSD(deal){
  const orgData = store.getState().organization.data;

  if(!orgData){
    //haven't loaded currencies yet
    return deal.estimatedValue || 0
  }

  const { currencies } = orgData;
  const { currency } = deal;

  if(!currency || !currencies || currency === 'USD' || currency === '$' || !currencies[currency]){
    return deal.estimatedValue || 0;
  }
  else{
    return Math.floor(deal.estimatedValue / currencies[currency]) || 0;
  }
}

export function dealAmountAsString(deal, convertToUSD = false){
  const currency = (deal && deal.currency) || '$';

  if(currency === '$'){
    //is in dollars
    return `$${formatThousands(deal.estimatedValue)}`;
  }
  else if(!convertToUSD){
    //show in local currency
    return `${currency} ${formatThousands(deal.estimatedValue)}`
  }
  else{
    return `$${formatThousands(dealValueInUSD(deal))}`;
  }
}

export function scrollTop(){
  setTimeout(() => {
    //https://www.w3schools.com/howto/howto_js_scroll_to_top.asp
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }, 50);
}

export function scrollTo(elementId){
  setTimeout(() => {
    //https://www.w3schools.com/jsref/met_element_scrollintoview.asp
    document.getElementById(elementId) && document.getElementById(elementId).scrollIntoView();
  }, 50);
}

export function getCoachabilityScoreFromQuestionsAndResponses(questions, sellerResponses, reviewerResponses){
  let total = 0;

  _.each(questions, question => {
    if(question.ratingType === 'tier'){
      total += Math.ceil((reviewerResponses[question.id] / 7) * 58);//100 - (7 * 3 * 2)
    }
    else if(question.ratingType === 'likert'){
      //likert questions = worth 1 each for manager + seller responses, 3 questions, = 42% of score
      total += sellerResponses[question.id];
      total += reviewerResponses[question.id];
    }
  });

  return total;
}

export function getCriticalExpertiseTotalScoreFromQuestionsAndResponses(questions, sellerResponses, reviewerResponses){
  let total = 0;

  _.each(questions, question => {
    if(question.ratingType === 'tier'){
      total += Math.ceil((reviewerResponses[question.id] / 7) * 30); //Get to choose here, we're going to go w/ 30% of score derived from this, 70% from likert questions
    }
    else if(question.ratingType === 'likert'){
      total += 0.70 * (getScoreOfOneHundredFromLikertScores(sellerResponses[question.id], reviewerResponses[question.id])) / _.size(_.filter(questions, { ratingType: 'likert' }));
    }
  });

  return Math.ceil(total);
}

export function getCompetencyScoreFromQuestionsAndResponses(questions, sellerResponses, reviewerResponses){

  let total = 0;

  //4,4,4,4,Overall:3 should place user at a score of "50" & a tier of 2, and anything below this should cause "low" coaching content to display

  _.each(questions, question => {
    if(question.ratingType === 'tier'){
      total += Math.ceil((reviewerResponses[question.id] / 7) * 44); //Math.pow(reviewerResponses[question.id] / 7, .5) * 44;//100 - (7 * 4 * 2)
    }
    else if(question.ratingType === 'likert'){
      //likert questions = worth 1 each for manager + seller responses
      total += sellerResponses[question.id];
      total += reviewerResponses[question.id];
    }
  });

  return total;
}

export function getTierFromScore(score){
  if(score < 40){
    return 1;
  }
  else if(score < 50){
    return 2;
  }
  else if(score < 70){
    return 3;
  }
  else if(score < 78){
    return 4;
  }
  else if(score < 85){
    return 5;
  }
  else if(score < 94){
    return 6;
  }
  else{
    return 7;
  }
}

export function getTierDetails(tier){
  switch(tier){
    case 7:
      return {
        displayTier: '1',
        percentile: 99,
        color: '#e4b43a',
      }
    case 6:
      return {
        displayTier: '2',
        percentile: 95,
        color: '#336ef2',
      }
    case 5:
      return {
        displayTier: '3',
        percentile: 90,
        color: '#4e78d7'
      }
    case 4:
      return {
        displayTier: '4',
        percentile: 75,
        color: '#6983bc'
      }
    case 3:
      return {
        displayTier: '5',
        percentile: 50,
        color: '#929896'
      }
    case 2:
      return {
        displayTier: '6',
        percentile: 25,
        color: '#a7636b'
      }
    case 1:
      return {
        displayTier: '7',
        percentile: 5,
        color: '#d2384b'
      }
  }
  return 7 - tier;//will results in tiers 6 (worst) - 0 (best)
}

export function getScoreOfOneHundredFromLikertScores(sellerLikert, reviewerLikert){
  //seller likert out of 7, worth 50% of score
  //reviewer likert out of 7, worth 50% of score
  //examples:
  //seller: 4, reviewer 6 -> 76
  //seller 7, reviewer 6 -> 90
  //seller 6, reviewer 7 -> 95

  return Math.ceil((sellerLikert * 50/7) + (reviewerLikert * 50/7));
}

export function getPredictiveSkillsReviewScoresFromReviewData(salesCompetencyReviewItems, expertiseReviewItems, review, priorReview){

  const {
    sellerResponses,
    reviewerResponses,
  } = review;

  const priorSellerResponses = (priorReview || {}).sellerResponses;
  const priorReviewerResponses = (priorReview || {}).reviewerResponses;

  const hasPriorScore = !!priorReview;

  // console.log('sellerResponses: ', sellerResponses);
  // console.log('reviewerResponses: ', reviewerResponses);
  //
  // console.log('priorSellerResponses: ', priorSellerResponses);
  // console.log('priorReviewerResponses: ', priorReviewerResponses);
  //
  // console.log('salesCompetencyReviewItems: ', salesCompetencyReviewItems);

  const scoresByCompetency = _.map(sellingCompetencies(), competency => {

    //TODO actuually use seller AND manager responses!
    const score = getCompetencyScoreFromQuestionsAndResponses(_.filter(salesCompetencyReviewItems, { competencyIndex: competency.id }), sellerResponses, reviewerResponses);
    const priorScore = hasPriorScore && getCompetencyScoreFromQuestionsAndResponses(_.filter(salesCompetencyReviewItems, { competencyIndex: competency.id }), priorSellerResponses, priorReviewerResponses);
    const likertItems = _.filter(salesCompetencyReviewItems, { competencyIndex: competency.id, ratingType: 'likert' });
    const sellerOnlyLikertTotal = _.reduce(likertItems, (memo, item) => { return memo + sellerResponses[item.id] }, 0);
    const reviewerOnlyLikertTotal = _.reduce(likertItems, (memo, item) => { return memo + reviewerResponses[item.id] }, 0);

    const delta = priorScore && Math.ceil(score) - Math.ceil(priorScore);

    return {
      id: competency.id,
      name: competency.name,
      score: Math.ceil(score),
      tier: getTierFromScore(score),
      diff: Math.ceil(reviewerOnlyLikertTotal - sellerOnlyLikertTotal),
      ...(delta && {delta}),
      ...(priorScore && { priorScore: Math.ceil(priorScore) }),
    }
  });

  const competencyScore = Math.ceil(_.reduce(scoresByCompetency, (memo, competencyScores) => {
    return memo + competencyScores.score;
  }, 0) / 10);
  const priorCompetencyScore = hasPriorScore && Math.ceil(_.reduce(scoresByCompetency, (memo, competencyScores) => {
    return memo + competencyScores.priorScore;
  }, 0) / 10);
  const competencyTier = getTierFromScore(competencyScore);
  const competencyDelta = hasPriorScore && competencyScore - priorCompetencyScore;

  const scoresByCriticalExpertise = _.map(expertiseReviewItems, expertiseItem => {

    const sellerScore = sellerResponses[expertiseItem.id];
    const reviewerScore = reviewerResponses[expertiseItem.id];

    const score = getScoreOfOneHundredFromLikertScores(sellerScore, reviewerScore);

    let priorScore, delta = false;

    if(priorSellerResponses){
      priorScore = getScoreOfOneHundredFromLikertScores(priorSellerResponses[expertiseItem.id], priorReviewerResponses[expertiseItem.id]);
      delta = score - priorScore;
    }

    return {
      id: expertiseItem.id,
      name: expertiseItem.name,
      sellerScore,
      reviewerScore,
      score,
      ...(priorScore && { priorScore }),
      //tier: Math.ceil((score / 100) * 7),
      tier: getTierFromScore(score),
      diff: reviewerScore - sellerScore,
      ...(delta && {delta}),
    }
  });

  const expertiseReviewItemsMappedToQuestions = _.map(expertiseReviewItems, item => {
    return {
      id: item.id,
      ratingType: 'likert',
    }
  });
  const expertiseOverallItemMappedToQuestion = {
    id: 'criticalexpertisetier',
    ratingType: 'tier'
  }
  const expertiseQuestions = _.union(expertiseReviewItemsMappedToQuestions, [expertiseOverallItemMappedToQuestion]);

  const expertiseScore = Math.ceil(getCriticalExpertiseTotalScoreFromQuestionsAndResponses(expertiseQuestions, sellerResponses, reviewerResponses));
  const priorExpertiseScore = hasPriorScore && Math.ceil(getCriticalExpertiseTotalScoreFromQuestionsAndResponses(expertiseQuestions, priorSellerResponses, priorReviewerResponses));
  const expertiseTier = getTierFromScore(expertiseScore);
  const expertiseDelta = hasPriorScore && (expertiseScore - priorExpertiseScore);

  const coachabilityItems = _.filter(salesCompetencyReviewItems, { questionType: 'coachability' });

  const coachabilityScore = getCoachabilityScoreFromQuestionsAndResponses(coachabilityItems, sellerResponses, reviewerResponses);
  const priorCoachabilityScore = hasPriorScore && getCoachabilityScoreFromQuestionsAndResponses(coachabilityItems, priorSellerResponses, priorReviewerResponses);
  const coachabilityTier = getTierFromScore(coachabilityScore);
  const coachabilityDelta = hasPriorScore && Math.ceil(coachabilityScore - priorCoachabilityScore);

  const overallScore = Math.ceil(.6 * competencyScore + .25 * expertiseScore + .15 * coachabilityScore);
  const priorOverallScore = hasPriorScore && Math.ceil(.6 * priorCompetencyScore + .25 * priorExpertiseScore + .15 * priorCoachabilityScore);
  const overallTier = getTierFromScore(overallScore);
  const overallDelta = hasPriorScore && (overallScore - priorOverallScore);

  const itemsForHighlights = _.union(scoresByCompetency, scoresByCriticalExpertise);
  const mostImproved = _.orderBy(_.filter(itemsForHighlights, item => { return item.delta && item.delta > 0 }), 'delta', 'desc');
  //const topStrengths = _.orderBy(itemsForHighlights, 'score', 'desc');
  //const topWeaknesses = _.orderBy(itemsForHighlights, 'score');
  const topCompetencies = _.orderBy(scoresByCompetency, 'score', 'desc');
  const bottomCompetencies = _.orderBy(scoresByCompetency, 'score');
  const topExpertise = _.orderBy(scoresByCriticalExpertise, 'score', 'desc');
  const bottomExpertise = _.orderBy(scoresByCriticalExpertise, 'score');

  //todo calculate these
  const underEstimated = _.orderBy(_.filter(itemsForHighlights, item => { return item.diff && item.diff > 0 }), 'diff', 'desc');
  const overEstimated = _.orderBy(_.filter(itemsForHighlights, item => { return item.diff && item.diff < 0 }), 'diff');

  const underEstimatedCompetencies = _.orderBy(_.filter(scoresByCompetency, item => { return item.diff && item.diff >= 3 }), 'diff', 'desc');
  const overEstimatedCompetencies = _.orderBy(_.filter(scoresByCompetency, item => { return item.diff && item.diff <= -3 }), 'diff');
  const underEstimatedExpertise = _.orderBy(_.filter(scoresByCriticalExpertise, item => { return item.diff && item.diff > 0 }), 'diff', 'desc');
  const overEstimatedExpertise = _.orderBy(_.filter(scoresByCriticalExpertise, item => { return item.diff && item.diff < 0 }), 'diff');

  const scores = {
    competencyScore,
    priorCompetencyScore,
    competencyTier,
    competencyDelta,

    expertiseScore,
    priorExpertiseScore,
    expertiseTier,
    expertiseDelta,

    coachabilityScore,
    priorCoachabilityScore,
    coachabilityTier,
    coachabilityDelta,

    overallScore,
    priorOverallScore,
    overallTier,
    overallDelta,

    scoresByCompetency,
    scoresByCriticalExpertise,

    mostImproved,
    topCompetencies,
    bottomCompetencies,
    underEstimated,
    overEstimated,
    topExpertise,
    bottomExpertise,

    underEstimatedCompetencies,
    overEstimatedCompetencies,
    underEstimatedExpertise,
    overEstimatedExpertise,
  }

  console.log('calculated scores: ', scores);

  return scores
}

export function getScoreDescriptions(score, competencyName){
  const tier = getTierFromScore(score);

  let description;

  if(tier <= 1){
    description = `You possess few strengths in COMPETENCY_PLACEHOLDER. We strongly advise attending all training sessions offered in this area, and asking your manager and fellow sellers for assistance when navigating this stretch of the deal.`
  }
  else if(tier <= 2){
    description = `You possess some strengths in COMPETENCY_PLACEHOLDER, but still have much to improve. We strongly advise attending all training sessions offered in this area, and asking your manager and fellow sellers for assistance when navigating this stretch of the deal.`
  }
  else{
    description = `You excel at COMPETENCY_PLACEHOLDER, and often your strengths in this competency result in consistently winning and should be emulated by other sellers at your organization. Seek out opportunities to help others who struggle navigating this portion of the deal, and don’t shy from sharing your secrets of the trade when it comes to COMPETENCY_PLACEHOLDER.`
  }

  return _.replace(description, /COMPETENCY_PLACEHOLDER/g, competencyName);
}

export function getLetterScoreForSprintActivityItems(items){

  if(!items || items.length === 0){
    return {
      adjustedPercentComplete: 0,
      letterGrade: 'I'
    }
  }

  const totalComplete = _.reduce(items, (memo, percentOfItemComplete) => {
    return memo + Math.min(percentOfItemComplete, 100)
  }, 0);
  const totalPossible = items.length * 100;
  const adjustedPercentComplete = Math.min(Math.ceil(totalComplete * 100 / totalPossible), 100);

  const thresholdForA = 90;
  const thresholdForB = 80;
  const thresholdForC = 70;
  const thresholdForD = 60;

  let letterGrade;

  if(adjustedPercentComplete > thresholdForA){
    letterGrade = 'A';
  }
  else if(adjustedPercentComplete > thresholdForB){
    letterGrade = 'B';
  }
  else if(adjustedPercentComplete > thresholdForC){
    letterGrade = 'C';
  }
  else if(adjustedPercentComplete > thresholdForD){
    letterGrade = 'D';
  }
  else{
    letterGrade = 'F';
  }

  return {
    adjustedPercentComplete,
    letterGrade,
  };
}
//window.getLetterScoreForSprintActivityItems = getLetterScoreForSprintActivityItems;

//http://www.chiragmehta.info/chirag/2009/10/02/algorithm-for-converting-15-digit-salesforce-id-to-18-digit/
export function normalizeSalesforceId(id){
  if (id == null){
    return id;
  }

  id = id.replace(/\"/g, '');

  if(id.length != 15){
    return null;
  }

  var suffix = "";

  for (var i = 0; i < 3; i++){
    var flags = 0;
    for (var j = 0; j < 5; j++){
      var c = id.charAt(i * 5 + j);
      if (c >= 'A' && c <= 'Z'){
        flags += 1 << j;
      }
    }
    if (flags <= 25){
      suffix += "ABCDEFGHIJKLMNOPQRSTUVWXYZ".charAt(flags);
    }
    else{
      suffix += "012345".charAt(flags-26);
    }
  }
  return id + suffix;
}
window.normalizeSalesforceId = normalizeSalesforceId;

//items: [2,3,0,0]...etc. all /3
//examples
/*
  2 2 1 => A
  3 3 0 => A
  3 1 0 => B
  2 1 0 => C
  1 1 0 => C
  1 0 0 => D
  0 0 0 => F
*/
export function getLetterScoreForSprintDevelopmentItems(items){

  if(!items || items.length === 0){
    return {
      adjustedPercentComplete: 0,
      letterGrade: 'I'
    }
  }

  const numberItemsAvailable = items.length;
  const totalComplete = _.sum(items);
  const totalPossible = items.length * 3;
  const percentComplete = totalComplete / totalPossible;

  const thresholdForA = 55;
  const thresholdForB = 40;
  const thresholdForC = 25;
  const thresholdForD = 0;

  //2 items = no adjustment
  //3 items = no adjustment
  //4 items = adjust by .90
  //5 items = adjust by .81
  const thresholdAdjustmentFactor = 1 * (Math.pow(.90, Math.max(numberItemsAvailable - 3, 0)));

  const adjustedPercentComplete = Math.min(Math.ceil(100 * percentComplete / thresholdAdjustmentFactor), 100);

  let letterGrade;

  if(adjustedPercentComplete > thresholdForA){
    letterGrade = 'A';
  }
  else if(adjustedPercentComplete > thresholdForB){
    letterGrade = 'B';
  }
  else if(adjustedPercentComplete > thresholdForC){
    letterGrade = 'C';
  }
  else if(adjustedPercentComplete > thresholdForD){
    letterGrade = 'D';
  }
  else{
    letterGrade = 'F';
  }

  return {
    adjustedPercentComplete,
    letterGrade
  };
}
window.getLetterScoreForSprintDevelopmentItems = getLetterScoreForSprintDevelopmentItems;

export function print(){
  if(getBrowserName() !== 'safari'){
    window.print();
  }
  else{
    //have to "stop" network requests immediately before attempting to print, otherwise safari
    //tries to wait for network requests to complete (which the never do)
    console.log('STOPPING page load in order to print in Safari');
    window.stop();
    window.print();
  }
}

export function getAppVersion(){
  return (!isProduction() ? (CONFIG.ENV + ' ') : 'BETA ') + 'V' + CONFIG.VERSION;
}

//UTC timestamp in seconds
export function getCurrentTimestamp(){
  //https://firebase.google.com/docs/firestore/manage-data/add-data
  return firebase.firestore.FieldValue.serverTimestamp();
}

export function getCurrentTime(){
  return moment().valueOf();
}

//https://plainjs.com/javascript/utilities/set-cookie-get-cookie-and-delete-cookie-5/
export function getCookie(name) {
  var v = document.cookie.match('(^|;) ?' + name + '=([^;]*)(;|$)');
  return v ? v[2] : null;
}
export function setCookie(name, value, days) {
  var d = new Date;
  d.setTime(d.getTime() + 24*60*60*1000*days);
  document.cookie = name + "=" + value + ";path=/;expires=" + d.toGMTString();
}
export function deleteCookie(name){
  setCookie(name, '', -1);
}

export function getBrowserName(){
  const browser = detect();

  return (browser || {}).name
}
export function getWindowDimensions(){
  //https://stackoverflow.com/questions/1766861/find-the-exact-height-and-width-of-the-viewport-in-a-cross-browser-way-no-proto

  var viewPortWidth;
  var viewPortHeight;

  // the more standards compliant browsers (mozilla/netscape/opera/IE7) use window.innerWidth and window.innerHeight
  if (typeof window.innerWidth != 'undefined') {
   viewPortWidth = window.innerWidth,
   viewPortHeight = window.innerHeight
  }

  // IE6 in standards compliant mode (i.e. with a valid doctype as the first line in the document)
  else if (typeof document.documentElement != 'undefined'
  && typeof document.documentElement.clientWidth !=
  'undefined' && document.documentElement.clientWidth != 0) {
    viewPortWidth = document.documentElement.clientWidth,
    viewPortHeight = document.documentElement.clientHeight
  }

  // older versions of IE
  else {
   viewPortWidth = document.getElementsByTagName('body')[0].clientWidth,
   viewPortHeight = document.getElementsByTagName('body')[0].clientHeight
  }
  return {
    width: viewPortWidth,
    height: viewPortHeight
  };
}
export function isUnsupportedDevice(){
  const dimensions = getWindowDimensions();
  return dimensions.width < 960 || dimensions.height < 600;
}
export function isUnsupportedBrowser(){
  const browserName = getBrowserName();

  if( browserName === 'chrome' ||
      browserName === 'firefox' ||
      browserName === 'safari'
  ){
    return false;
  }

  return true;
}

export function callIfFunction(callback, props = undefined){
  if(typeof callback === 'function'){
    callback(props);
  }
}

export function momentIsBetweenDatesInclusive(compare, start, end){
  const oneBeforeStart = moment(start).subtract('days', 1);
  const oneAfterEnd = moment(end).add('days', 1);

  return moment(compare).isBetween(oneBeforeStart, oneAfterEnd);
}

export function getSprintStatusTextForEmployee(status){
  console.log('getSprintStatusTextForEmployee: ', status);
  if(status === 'editing'){
    return 'Editing sprint';
  }
  else if(status === 'submitted'){
    return 'Sprint submitted';
  }
  else{
    return 'Sprint not setup';
  }
}

export function getCurrentMonthCountInQuarter(quarterOneStartMonthOffset = 0, dateToCheck){

  const currentMonthAsNumber = parseInt(moment(dateToCheck).format('M'));

  if((currentMonthAsNumber - quarterOneStartMonthOffset) % 3 === 1){
    return 'first';
  }
  else if((currentMonthAsNumber - quarterOneStartMonthOffset) % 3 === 2){
    return 'second';
  }
  else if((currentMonthAsNumber - quarterOneStartMonthOffset) % 3 === 0){
    return 'third';
  }
  else{
    console.warn('Could not determine current month count in quarter.');
    return 'first';
  }
}

export function getMonthCountInQuarterForDate(date, quarterOneStartMonthOffset = 0){
  const currentMonthAsNumber = parseInt(moment(date).format('M'));

  if((currentMonthAsNumber - quarterOneStartMonthOffset) % 3 === 1){
    return 'first';
  }
  else if((currentMonthAsNumber - quarterOneStartMonthOffset) % 3 === 2){
    return 'second';
  }
  else if((currentMonthAsNumber - quarterOneStartMonthOffset) % 3 === 0){
    return 'third';
  }
  else{
    console.warn('Could not determine current month count in quarter.');
    return 'first';
  }
}

export function getCategoryNameFromId(categoryId){
  switch(categoryId){
    default:
      return undefined;
    case 1:
    case '1':
      return 'motivation';
    case 2:
    case '2':
      return 'interaction';
    case 3:
    case '3':
      return 'cognition';
    case 4:
    case '4':
      return 'execution';
  }
}

export function getCategoryFromDimensionId(dimensionId){

  const matchingDimension = _.find(dimensions, { id: dimensionId });

  if(!matchingDimension){
    return undefined;
  }

  const matchingDimensionCategoryId = matchingDimension.categoryId;

  switch(matchingDimensionCategoryId){
    default:
      return undefined
    case '1':
      return {
        name: 'motivation',
        id: '1',
      }
    case '2':
      return {
        name: 'interaction',
        id: '2',
      }
    case '3':
      return {
        name: 'cognition',
        id: '3',
      }
    case '4':
      return {
        name: 'execution',
        id: '4',
      }
  }
}

export function getDimensionsForCategory(scoredDimensionIds, cateogryId){
  const dimensionCategoryScoredComponents = _.filter(scoredDimensionIds, dimensionId => {
    return getCategoryFromDimensionId(dimensionId) && getCategoryFromDimensionId(dimensionId).id === cateogryId;
  });

  return dimensionCategoryScoredComponents || [];
}

export function actionPlanSetupForSprint(sprint){
  if(!sprint || !sprint.goals || !sprint.goals[0]){
    return false;
  }

  const goalExistsWithoutActionPlan = _.find(sprint.goals, goal => {
    //if behavior or skill goal, and no action plan or smart goal configured
    return  (goal.category === 'behavior' || goal.category === 'skill' || goal.category === 'competency' || goal.category === 'expertise') &&
            (!goal.goalMeta ||
            !goal.goalMeta.actionPlan ||
            goal.goalMeta.actionPlan === '' ||
            !goal.goalMeta.smartGoal ||
            goal.goalMeta.smartGoal === '');
  }) ? true : false;

  if(goalExistsWithoutActionPlan){
    return false;
  }
  else{
    //at least one goal and no goals exists without action plan
    return true;
  }
}

export function firstWeeklyReviewConductedForSprint(sprint){
  if(!sprint || !sprint.goals || !sprint.goals[0] || !sprint.thisWeekActionItems || sprint.thisWeekActionItems === ''){
    return false;
  }
  else{
    return true;
  }
}

export function formatNewLines(text){
  if(text){
    return text.replace(/(\r\n|\n|\r)/gm, '<br />');
  }
  else{
    return '';
  }
}

export function getRatingClass(rating, maxRating){
  const ratingAsPercentage = (rating / (maxRating || 5)) * 100.0;

  // console.log('ratingAsPercentage', ratingAsPercentage);

  if(ratingAsPercentage >= 90){
    return 'perfect';
  }
  else if(ratingAsPercentage >= 75){
    return 'great';
  }
  else if(ratingAsPercentage >= 50){
    return 'average';
  }
  else if(ratingAsPercentage >= 25){
    return 'below-average';
  }
  else{
    return 'poor';
  }
}

export function getDefaultTalentRatingExplanationForValue(val){
  console.log('getDefaultTalentRatingExplanations: ', val - 1);
  return getDefaultTalentRatingExplanations()[val - 1];
}

export function getDefaultTalentRatingExplanations(){
  // return [
  //   'Does not apply or is extremely ineffective',
  //   'Rarely applies or is ineffective',
  //   'Infrequently applies with average effectiveness',
  //   'Sometimes applies with average effectiveness',
  //   'Often applies and is moderately effective',
  //   'Often applies and is highly effective',
  //   'Consistently applies and is extremely effective'
  // ];
  return [
    'Extremely Ineffective',
    'Very Ineffective',
    'Below Average Effectiveness',
    'Average Effectiveness',
    'Above Average Effectiveness',
    'Very Effective',
    'Extremely Effective',
  ];
}

//Temp function for converting to new date system:
// window.getReadableDate = function(dateInMS){
//
//   console.log('date in MS: ', moment(dateInMS).format('dddd MMMM Do'));
//   console.log('converted to: ', moment(dateInMS).format('YYYYMMDD'));
// }

//for testing in console:
// fromDate = '2/14/19';
// console.log('start on: ', moment(calculateLikelySprintStartAndEndDatesFromCurrentDate(fromDate).startDate).format('MM/DD/YYYY'));
// console.log('end on: ', moment(calculateLikelySprintStartAndEndDatesFromCurrentDate(fromDate).endDate).format('MM/DD/YYYY'));
//window.calculateLikelySprintStartAndEndDatesFromCurrentDate = calculateLikelySprintStartAndEndDatesFromCurrentDate;
// window.moment = moment;
export function calculateLikelySprintStartAndEndDatesFromCurrentDate(fromDate = moment()){

  //Monday-Wed should consider the last week as the start/end dates
  //Thurs-Sun should consider the current week as the start/end dates

  //day of week from current user's timezone
  const dayOfWeek = moment(fromDate).day();

  //M-W
  if(dayOfWeek === 1 || dayOfWeek === 2 || dayOfWeek === 3){
    return {
      startDate: moment(fromDate).startOf('isoWeek').subtract(7, 'days').format('YYYYMMDD'), //Last Monday
      endDate: moment(fromDate).startOf('isoWeek').subtract(7, 'days').add(4, 'days').format('YYYYMMDD'), //Last Friday
    }
  }
  //Th-Su
  else{
    return {
      startDate: moment(fromDate).startOf('isoWeek').format('YYYYMMDD'), //This monday
      endDate: moment(fromDate).startOf('isoWeek').add(4, 'days').format('YYYYMMDD'), //This friday
    }
  }
}

export function focusAfterTimeout(self, ref, timeout = 50){
  setTimeout(() => {
    // console.log('attempting focus');
    // console.log(ref);
    // console.log(self[ref]);
    // console.log(self[ref].focus);
    self[ref] && self[ref].focus && self[ref].focus();
  }, 50);
}

export function isNumerical(string){
  const numbersOnlyRegex = /^\d+$/;
  return numbersOnlyRegex.test(string);
}

export function formatThousands(num = '', decimals = 0){
  //null fix
  num = num || ''
  const formattedNumberAsString = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  if(formattedNumberAsString.indexOf('.') !== -1){
    //need to format decimals
    const wholeNumber = formattedNumberAsString.split('.')[0];
    const decimalsPart = formattedNumberAsString.split('.')[1];

    if(decimals === 0){
      return wholeNumber;
    }
    else{
      return wholeNumber + '.' + decimalsPart.substring(0, decimals);
    }
  }
  else{
    return formattedNumberAsString
  }
}

export function goalValueIsSet(goalValue){
  return goalValue !== undefined && goalValue !== '';
}

export function organizationGoalsAreDirty(cachedGoals, updatedGoals){
  let isDirty = false;

  _.each(cachedGoals, cachedGoal => {
    const matchingUpdatedGoal = _.find(updatedGoals, updatedGoal => {
      return updatedGoal.id === cachedGoal.id;
    });

    if(!matchingUpdatedGoal){
      isDirty = true;
    }
    else{
      // console.log('checking ', cachedGoal.ratings);
      // console.log('with ', matchingUpdatedGoal.ratings);

      //check whether goal inactive same in both
      if( cachedGoal.inactive !== matchingUpdatedGoal.inactive ||
          cachedGoal.name !== matchingUpdatedGoal.name ||
          cachedGoal.description !== matchingUpdatedGoal.description ||
          cachedGoal.definition !== matchingUpdatedGoal.definition ||
          !_.isEqual(cachedGoal.ratings, matchingUpdatedGoal.ratings)
      ){
        isDirty = true;
      }
    }
  });

  _.each(updatedGoals, updatedGoal => {
    const matchingCachedGoal = _.find(cachedGoals, cachedGoal => {
      return cachedGoal.id === updatedGoal.id;
    });

    if(!matchingCachedGoal){
      isDirty = true;
    }
    else{
      //check whether goal inactive same in both
      if( updatedGoal.inactive !== matchingCachedGoal.inactive ||
        updatedGoal.name !== matchingCachedGoal.name ||
        updatedGoal.description !== matchingCachedGoal.description ||
        updatedGoal.definition !== matchingCachedGoal.definition ||
        !_.isEqual(updatedGoal.ratings, matchingCachedGoal.ratings)
      ){
        isDirty = true;
      }
    }
  });

  return isDirty;
}

export function sprintGoalsAreDirty(cachedGoals, updatedGoals){

  let isDirty = false;

  _.each(cachedGoals, cachedGoal => {
    const matchingUpdatedGoal = _.find(updatedGoals, updatedGoal => {
      return updatedGoal.id === cachedGoal.id;
    });

    if(!matchingUpdatedGoal){
      isDirty = true;
    }
    else{
      //check goals against eachother
      if(cachedGoal.goalTarget !== matchingUpdatedGoal.goalTarget){
        isDirty = true;
      }
      else if(goalValueIsSet(cachedGoal.goalValue) || goalValueIsSet(matchingUpdatedGoal.goalValue)){
        //if either value is set, compare
        if(cachedGoal.goalValue !== matchingUpdatedGoal.goalValue){
          isDirty = true;
        }
      }
      else{
        //else...check to make sure they're both falsy
        if(!(!cachedGoal.goalValue && !matchingUpdatedGoal.goalValue)){
          isDirty = true;
        }
      }

      //check multiple targets if exist
      if(cachedGoal.goalTargets || matchingUpdatedGoal.goalTargets){
        if(!_.isEqual(cachedGoal.goalTargets, matchingUpdatedGoal.goalTargets)){
          isDirty = true;
        }
      }

      //check meta values
      if(cachedGoal.goalMeta || matchingUpdatedGoal.goalMeta){
        if(!_.isEqual(cachedGoal.goalMeta, matchingUpdatedGoal.goalMeta)){
          isDirty = true;
        }
      }
      if(cachedGoal.customGoalValues || matchingUpdatedGoal.customGoalValues){
        if(!_.isEqual(cachedGoal.customGoalValues, matchingUpdatedGoal.customGoalValues)){
          isDirty = true;
        }
      }
    }
  });

  _.each(updatedGoals, updatedGoal => {
    const matchingCachedGoal = _.find(cachedGoals, cachedGoal => {
      return cachedGoal.id === updatedGoal.id;
    });

    if(!matchingCachedGoal){
      isDirty = true;
    }
    else{
      //check goals against eachother
      if(updatedGoal.goalTarget !== matchingCachedGoal.goalTarget){
        isDirty = true;
      }
      else if(goalValueIsSet(updatedGoal.goalValue) || goalValueIsSet(matchingCachedGoal.goalValue)){
        //if either value is set, compare
        if(updatedGoal.goalValue !== matchingCachedGoal.goalValue){
          isDirty = true;
        }
      }
      else{
        //else...check to make sure they're both falsy
        if(!(!updatedGoal.goalValue && !matchingCachedGoal.goalValue)){
          isDirty = true;
        }
      }

      //check meta values
      if(updatedGoal.goalMeta || matchingCachedGoal.goalMeta){
        if(!_.isEqual(updatedGoal.goalMeta, matchingCachedGoal.goalMeta)){
          isDirty = true;
        }
      }
    }
  });

  return isDirty;
}

export function getScoreClassificationInOrderedGroupScores(score, orderedGroupScores){
  let scoreClassification = 'none';

  // console.log('orderedGroupScores: ', orderedGroupScores);

  if(orderedGroupScores.length === 3){
    //determine based on thresholds
    if(Math.round(score) >= 38){
      scoreClassification = 'high';
    }
    else if(Math.round(score) <= 28){
      scoreClassification = 'low';
    }
  }
  else if(orderedGroupScores.length === 4){
    //determine based on thresholds
    if(Math.round(score) >= 28){
      scoreClassification = 'high';
    }
    else if(Math.round(score) <= 18){
      scoreClassification = 'low';
    }
  }
  else if(orderedGroupScores.length === 5){
    //determine based on thresholds
    if(Math.round(score) >= 25){
      scoreClassification = 'high';
    }
    else if(Math.round(score) <= 15){
      scoreClassification = 'low';
    }
  }



  //take top and bottom
  // if(Math.round(orderedGroupScores[0]) === Math.round(score)){
  //   scoreClassification = 'high';
  // }
  // else if(Math.round(orderedGroupScores[orderedGroupScores.length - 1]) === Math.round(score)){
  //   scoreClassification = 'low';
  // }

  return scoreClassification;
}

//https://stackoverflow.com/questions/37065663/array-of-object-deep-comparison-with-lodash
export function isArrayEqual(x, y) {
  return _(x).xorWith(y, _.isEqual).isEmpty();
};

//https://stackoverflow.com/questions/4878756/how-to-capitalize-first-letter-of-each-word-like-a-2-word-city/4878797
export function toTitleCase(str) {
    return str.replace(/\w\S*/g, function(txt){
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export function getOptionValuesForSelect(options, values){

  return _.map(values, valueToFind => {
    return _.find(options, option => {
      return option.value === valueToFind;
    });
  });
}

export function getOptionValueForSelect(options, value){
  return _.find(options, option => {
    return option.value === value;
  });
}

export function getFourboxCategories(sellingCompetenciesEnabled = false, orderForAdmin = false){

  if(sellingCompetenciesEnabled){
    if(orderForAdmin){
      return [{
        name: 'Selling Competencies',
        key: 'competency'
      },{
        name: 'Critical Expertise',
        key: 'expertise'
      },{
        name: 'Activities',
        key: 'activity'
      },{
        name: 'Key Outcomes',
        key: 'outcome'
      }];
    }
    else{
      return [{
        name: 'Selling Competencies',
        key: 'competency'
      },{
        name: 'Activities',
        key: 'activity'
      },{
        name: 'Critical Expertise',
        key: 'expertise'
      },{
        name: 'Key Outcomes',
        key: 'outcome'
      }];
    }
  }
  else if(!orderForAdmin){
    return [{
      name: 'Behaviors',
      key: 'behavior'
    },{
      name: 'Activities',
      key: 'activity'
    },{
      name: 'Sales Skills',
      key: 'skill'
    },{
      name: 'Key Outcomes',
      key: 'outcome'
    }];
  }
  else{
    return [{
      name: 'Behaviors',
      key: 'behavior'
    },{
      name: 'Sales Skills',
      key: 'skill'
    },{
      name: 'Activities',
      key: 'activity'
    },{
      name: 'Key Outcomes',
      key: 'outcome'
    }];
  }
}

export function getFourboxTalentCategories(sellingCompetenciesEnabled = false){
  if(sellingCompetenciesEnabled){
    return [{
      name: 'Selling Competencies',
      key: 'competency'
    },{
      name: 'Critical Expertise',
      key: 'expertise'
    }];
  }
  else{
    return [{
      name: 'Behaviors',
      key: 'behavior'
    },{
      name: 'Sales Skills',
      key: 'skill'
    }];
  }
}

export function getCategoryComponents(category){
  switch(category){
    case 'motivation':
      return ['ambition', 'persistence', 'competition', 'confidence'];
    break;
    case 'interaction':
      return ['networker', 'individualizer', 'advisor', 'team'];
    break;
    case 'cognition':
      return ['learning agility', 'analytical', 'adaptability', 'resourcefulness'];
    break;
    case 'execution':
      return ['focus', 'persuasion', 'responsibility', 'discipline'];
    break;
    default:
      return [];
    break;
  }
}

export function getBechtelBenchmarkForTalent(talent, role){

  let benchmark = 100;

  // console.log(`computing benchmark for talent=${talent}`);

  if(role === 'M&BD'){
    switch(talent){
      case 'growth': benchmark = 70; break;
      case 'performance': benchmark = 72; break;
      case 'competition': benchmark = 64; break;
      case 'resilience': benchmark = 73; break;

      case 'quality': benchmark = 72; break;
      case 'developer': benchmark = 77; break;
      case 'communicator': benchmark = 83; break;
      case 'customer focus': benchmark = 76; break;

      case 'analytical': benchmark = 68; break;
      case 'strategic': benchmark = 72; break;
      case 'learning agility': benchmark = 74; break;
      case 'problem solver': benchmark = 71; break;

      case 'focus': benchmark = 75; break;
      case 'persuasion': benchmark = 68; break;
      case 'operational': benchmark = 64; break;
      case 'accountability': benchmark = 77; break;
    }
  }
  else if(role === 'BD Manager'){
    switch(talent){
      case 'confidence': benchmark = 71; break;
      case 'performance': benchmark = 75; break;
      case 'competition': benchmark = 59; break;
      case 'persistence': benchmark = 73; break;

      case 'networker': benchmark = 69; break;
      case 'relator': benchmark = 74; break;
      case 'developer': benchmark = 80; break;
      case 'collaborator': benchmark = 81; break;
      case 'customer focus': benchmark = 76; break;

      case 'analytical': benchmark = 73; break;
      case 'adaptability': benchmark = 75; break;
      case 'learning agility': benchmark = 78; break;
      case 'problem solver': benchmark = 79; break;

      case 'focus': benchmark = 77; break;
      case 'persuasion': benchmark = 69; break;
      case 'discipline': benchmark = 67; break;
      case 'accountability': benchmark = 79; break;
    }
  }
  else{
    switch(talent){
      case 'ambition': benchmark = 73; break;
      case 'persistence': benchmark = 72; break;
      case 'responsiveness': benchmark = 78; break;

      case 'networker': benchmark = 76; break;
      case 'collaborator': benchmark = 83; break;
      case 'relator': benchmark = 81; break;

      case 'analytical': benchmark = 72; break;
      case 'learning agility': benchmark = 79; break;
      case 'problem solver': benchmark = 82; break;

      case 'focus': benchmark = 78; break;
      case 'discipline': benchmark = 74; break;
      case 'accountability': benchmark = 82; break;
    }
  }

  return benchmark;
}

export function getWestinghouseBenchmarkForTalent(talent, role){

  let benchmark = 100;

  // console.log(`computing benchmark for talent=${talent}`);

  if(role === 'RVP/MD'){
    switch(talent){
      case 'growth': benchmark = 77; break;
      case 'performance': benchmark = 76; break;
      case 'competition': benchmark = 63; break;
      case 'resilience': benchmark = 77; break;

      case 'networker': benchmark = 75; break;
      case 'developer': benchmark = 82; break;
      case 'communication': benchmark = 85; break;
      case 'relator': benchmark = 78; break;

      case 'analytical': benchmark = 72; break;
      case 'strategic': benchmark = 77; break;
      case 'learning agility': benchmark = 79; break;
      case 'resourcefulness': benchmark = 75; break;


      case 'responsibility': benchmark = 76; break;
      case 'persuasion': benchmark = 69; break;
      case 'quality': benchmark = 75; break;
      case 'operational': benchmark = 67; break;
      case 'focus': benchmark = 80; break;
    }
  }
  else if(role === 'KAVP'){
    switch(talent){
      case 'responsiveness': benchmark = 78; break;
      case 'performance': benchmark = 76; break;
      case 'competition': benchmark = 61; break;
      case 'resilience': benchmark = 75; break;

      case 'networker': benchmark = 72; break;
      case 'collaboration': benchmark = 86; break;
      case 'advisor': benchmark = 85; break;
      case 'relator': benchmark = 80; break;

      case 'analytical': benchmark = 75; break;
      case 'strategic': benchmark = 79; break;
      case 'learning agility': benchmark = 80; break;
      case 'resourcefulness': benchmark = 81; break;

      case 'responsibility': benchmark = 80; break;
      case 'persuasion': benchmark = 71; break;
      case 'focus': benchmark = 81; break;
      case 'discipline': benchmark = 72; break;
    }
  }
  else if(role === 'CAD/CAM'){
    switch(talent){
      case 'responsiveness': benchmark = 71; break;
      case 'confidence': benchmark = 76; break;
      case 'competition': benchmark = 69; break;
      case 'ambition': benchmark = 75; break;

      case 'networker': benchmark = 71; break;
      case 'individualizer': benchmark = 73; break;
      case 'advisor': benchmark = 84; break;
      case 'collaboration': benchmark = 82; break;

      case 'analytical': benchmark = 81; break;
      case 'adaptability': benchmark = 73; break;
      case 'resourcefulness': benchmark = 77; break;

      case 'responsibility': benchmark = 81; break;
      case 'persuasion': benchmark = 67; break;
      case 'focus': benchmark = 80; break;
      case 'discipline': benchmark = 72; break;
    }
  }
  else if(role === 'CAE'){
    switch(talent){
      case 'ambition': benchmark = 71; break;
      case 'resilience': benchmark = 78; break;
      case 'responsiveness': benchmark = 75; break;

      case 'networker': benchmark = 78; break;
      case 'collaboration': benchmark = 87; break;
      case 'individualizer': benchmark = 79; break;

      case 'resourcefulness': benchmark = 87; break;
      case 'learning agility': benchmark = 86; break;

      case 'focus': benchmark = 83; break;
      case 'discipline': benchmark = 76; break;
      case 'responsibility': benchmark = 81; break;
    }
  }
  else if(role === 'RSM'){
    switch(talent){
      case 'resilience': benchmark = 76; break;
      case 'confidence': benchmark = 79; break;
      case 'competition': benchmark = 67; break;
      case 'ambition': benchmark = 71; break;

      case 'networker': benchmark = 76; break;
      case 'individualizer': benchmark = 78; break;
      case 'advisor': benchmark = 83; break;
      case 'collaboration': benchmark = 85; break;

      case 'learning agility': benchmark = 80; break;
      case 'analytical': benchmark = 75; break;
      case 'adaptability': benchmark = 79; break;
      case 'resourcefulness': benchmark = 79; break;

      case 'responsibility': benchmark = 81; break;
      case 'focus': benchmark = 75; break;
      case 'discipline': benchmark = 76; break;
    }
  }
  else{
    return null;
  }

  return benchmark;
}

export function getWestinghouseCategoryComponents(category, role){

  //console.log('getWestinghouseCategoryComponents for ', category, role);

  let components = [];

  switch(category){
    case 'motivation':
      components =
      role === "RVP/MD" ? ['growth', 'performance', 'competition', 'resilience'] :
      (role === "KAVP" ? ['responsiveness', 'performance', 'competition', 'resilience'] :
      (role === "CAD/CAM" ? ['responsiveness', 'confidence', 'competition', 'ambition'] :
      (role === "CAE" ? ['ambition', 'resilience', 'responsiveness'] :
      ['resilience', 'confidence', 'competition', 'ambition'] ) ) );//RSM
    break;
    case 'interaction':
      components =
      role === "RVP/MD" ? ['networker', 'developer', 'communication', 'relator'] :
      (role === "KAVP" ? ['networker', 'collaboration', 'advisor', 'relator'] :
      (role === "CAD/CAM" ? ['networker', 'individualizer', 'advisor', 'collaboration'] :
      (role === "CAE" ? ['networker', 'collaboration', 'individualizer'] :
      ['networker', 'individualizer', 'advisor', 'collaboration'] ) ) );//RSM
    break;
    case 'cognition':
      components =
      role === "RVP/MD" ? ['analytical', 'strategic', 'learning agility', 'resourcefulness'] :
      (role === "KAVP" ? ['analytical', 'strategic', 'learning agility', 'resourcefulness'] :
      (role === "CAD/CAM" ? ['analytical', 'adaptability', 'resourcefulness'] :
      (role === "CAE" ? ['resourcefulness', 'learning agility'] :
      ['learning agility', 'analytical', 'adaptability', 'resourcefulness'] ) ) );//RSM
    break;
    case 'execution':
      components =
      role === "RVP/MD" ? ['responsibility', 'persuasion', 'quality', 'operational', 'focus'] :
      (role === "KAVP" ? ['responsibility', 'persuasion', 'focus', 'discipline'] :
      (role === "CAD/CAM" ? ['responsibility', 'persuasion', 'focus', 'discipline'] :
      (role === "CAE" ? ['focus', 'discipline', 'responsibility'] :
      ['responsibility', 'focus', 'discipline'] ) ) );//RSM
    break;
  }

  return components;
}

export function getBechtelCategoryComponents(category, role){

  let components = [];

  switch(category){
    case 'motivation':
      components =
      role === "M&BD" ? ['performance', 'growth', 'resilience', 'competition'] :
      (role === "BD Manager" ? ['performance', 'persistence', 'competition', 'confidence'] :
      ['persistence', 'ambition', 'responsiveness'] );
    break;
    case 'interaction':
      components =
      role === "M&BD" ? ['developer', 'communicator', 'customer focus', 'networker'] :
      (role === "BD Manager" ? ['relator', 'developer', 'customer focus', 'networker', 'collaborator'] :
      ['relator', 'networker', 'collaborator'] );
    break;
    case 'cognition':
      components =
      role === "M&BD" ? ['analytical', 'strategic', 'learning agility', 'problem solver'] :
      (role === "BD Manager" ? ['analytical', 'learning agility', 'adaptability', 'problem solver'] :
      ['analytical', 'learning agility', 'problem solver'] );
    break;
    case 'execution':
      components =
      role === "M&BD" ? ['focus', 'accountability', 'persuasion', 'operational', 'quality'] :
      (role === "BD Manager" ? ['focus', 'accountability', 'persuasion', 'discipline'] :
      ['focus', 'accountability', 'discipline'] );
    break;
  }

  return components;
}

export function getWestinghouseDimensionIdForTalent(talent){
  // switch(talent){
  //
  // }

  return toTitleCase(talent);
}

export function getBechtelDimensionIdForTalent(talent){
  switch(talent){
    //Motivation
    case 'performance': return 'Performance';
    case 'growth': return 'Growth';
    case 'persistence': return 'Persistence';
    case 'resilience': return 'Resilience';
    case 'competition': return 'Competition';
    case 'confidence': return 'Confidence';
    case 'ambition': return 'Ambition';
    case 'responsiveness': return 'Responsiveness';

    //Interaction
    case 'relator': return 'Relator';
    case 'developer': return 'Developer';
    case 'communicator': return 'Communicator';
    case 'customer focus': return 'Customer Focus';
    case 'networker': return 'Networker';
    case 'collaborator': return 'Collaborator';

    //cognition
    case 'analytical': return 'Analytical';
    case 'strategic': return 'Strategic';
    case 'learning agility': return 'Learning Agility';
    case 'adaptability': return 'Adaptability';
    case 'problem solver': return 'Problem Solver';

    //execution
    case 'focus': return 'Focus';
    case 'accountability': return 'Accountability';
    case 'persuasion': return 'Persuasion';
    case 'discipline': return 'Discipline';
    case 'operational': return 'Operational';
    case 'quality': return 'Quality';


    default://todo remove this
      return null;
  }
}

export function getDimensionIdForTalent(talent){
  switch(talent){
    //Motivation
    case 'confidence':
      return 'ExpectationMean';
      break;
    case 'ambition':
      return 'AchievementMean';
      break;
    case 'competition':
      return 'IntensityMean';
      break;
    case 'persistence':
      return 'PersistenceMean';
      break;

    //Interaction
    case 'networker':
      return 'InitiatorMean';
      break;
    // case 'responsiveness':
    //   return 'ServiceMean';
    //   break;
    case 'individualizer':
      return 'RelatorMean';
      break;
    case 'advisor':
      return 'DeveloperMean';
      break;
    case 'team':
      return 'TeamMean';
      break;

    //Cognition
    case 'analytical':
      return 'AnalyticalMean';
      break;
    case 'adaptability':
      return 'AdaptabilityMean';
      break;
    case 'learning agility':
      return 'CommonSenseMean';
      break;
    case 'resourcefulness':
      return 'ProblemSolverMean';
      break;

    //Execution
    case 'focus':
      return 'DirectionMean';
      break;
    case 'responsibility':
      return 'ResponsibilityMean';
      break;
    case 'persuasion':
      return 'CourageMean';
      break;
    case 'discipline':
      return 'StructureMean';
      break;
    break;
  }
}

export function colorForCategoryDeprecated(category){
  switch(category){
    case '1':
    case 'motivation':
      return '#1A1F46';
    break;
    case '2':
    case 'interaction':
      return '#F3CF6A';
    break;
    case '3':
    case 'cognition':
      return '#A8272C';
    break;
    case '4':
    case 'execution':
      return '#81A65E';
    break;
    default:
      return '#041914';
    break;
  }
}

export function colorForCategory(category){
  switch(category){
    case '1':
    case 'motivation':
      return '#1A63FC';
    break;
    case '2':
    case 'interaction':
      return '#50E3C2';
    break;
    case '3':
    case 'cognition':
      return '#20AF46';
    break;
    case '4':
    case 'execution':
      return '#010080';
    break;
    default:
      return '#00011D';
    break;
  }
}

export function sellingCompetencies(){
  return competencies;
}

export function getSellingCompetency(id){
  return _.find(competencies, { id }) || {};
}

export function getDimensionFromId(dimensionId){
  return dimensions[dimensionId];
}
export function getDimensionFamilyFromId(dimensionFamilyId){
  return dimensionFamilies[dimensionFamilyId];
}
export function getDimensionFamilyFromDimensionId(dimensionId){
  const dimension = getDimensionFromId(dimensionId);
  return dimensionFamilies[dimension.dimensionFamily];
}
export function getDimensionsForFamily(dimensionFamiliy){
  return _.filter(dimensions, dimension => {
    return dimension.dimensionFamily === dimensionFamiliy;
  });
}

export function getDimensionFamilies(){
  return dimensionFamilies;
}

export function getCurrentUser(){
  // console.log('calling getCurrentUser');
  // console.log(firebase.auth().currentUser);
  return firebase.auth().currentUser;
}

export function organizationId(){
  const currentUser = store.getState().user;
  const orgConfig = store.getState().orgConfig.data;

  // console.log('currentUser: ', currentUser);

  if(orgConfig && orgConfig._id){
    return orgConfig._id;
  }
  else if(currentUser && currentUser.organizationId){
    return currentUser.organizationId;
  }
  else if(currentUser && currentUser.organization){
    return currentUser.organization;
  }
  else if(currentUser && currentUser.assessmentOrgId){
    return currentUser.assessmentOrgId;
  }
  else if(currentUser && currentUser.viewingOrganization){
    // console.log('viewing organization: ', currentUser.viewingOrganization);
    return currentUser.viewingOrganization;
  }
  else{
    return null;
  }
}

export function accountId(){
  const currentUser = firebase.auth().currentUser;

  if(currentUser){
    return currentUser.uid;
  }
  else{
    return null;
  }
}
window.uid = accountId;

export function salesforceIntegrationEnabled(){
  const currentOrganization = store.getState().organization.data;

  if(currentOrganization && currentOrganization.integrations && currentOrganization.integrations.salesforce && currentOrganization.integrations.salesforce.status === 'live'){
    return true;
  }
  else{
    return false;
  }
}

export function currentUserRole(){
  const currentUser = store.getState().user;

  //console.log('currentUserRole evaluated: ', currentUser);

  if(currentUser){
    return currentUser.role;
  }
  else{
    return null;
  }
}

export function currentUserIsEmployee(){
  return currentUserRole() === 'employee' || false;
}
export function currentUserIsManager(){
  return currentUserRole() === 'manager' || false;
}
export function currentUserIsSalesLeader(){
  return currentUserRole() === 'salesleader' || false;
}
export function currentUserIsAdmin(){
  return currentUserRole() === 'admin' || currentUserRole() === 'superadmin' || false;
}
export function currentUserIsAssessmentAdmin(){
  return currentUserRole() === 'assessmentadmin' || false;
}
export function currentUserIsSuperAdmin(){
  return currentUserRole() === 'superadmin' || false;
}

export function currentUserIsAdminType(){
  return currentUserRole() === 'superadmin' || currentUserRole() === 'admin' || currentUserRole() === 'assessmentadmin' || false;
}

export function currentUserHasPermission(permission){
  switch(permission){
    default:
      return false;
    case 'view_people_page':
      return currentUserIsAdmin();
    case 'view_teams_page':
      return currentUserIsManager() || currentUserIsSalesLeader() || currentUserIsAdmin();
    case 'manage_organizations':
      return currentUserIsSuperAdmin();
    case 'manage_global_settings':
      return currentUserIsSuperAdmin();
  }
}

//https://stackoverflow.com/questions/1349404/generate-random-string-characters-in-javascript
function generateRandomCharacter(){
  const possible = "abcdefghijklmnopqrstuvwxyz";
  return possible.charAt(Math.floor(Math.random() * possible.length));
}

export function generateUnsafeHash(){
  return generateRandomCharacter() +  Math.random().toString(36).substring(3, 8);
}
window.generateUnsafeHash = generateUnsafeHash;
window.generateUnsafeHashes = function(){
  for(let i = 0;i<25;i++){
    console.log(generateUnsafeHash());
  }
};
window.generateUniqueUnsafeHashesRows = function(count = 200){
  let uhs = '';
  let uniqueCheck = [];

  for(let i = 0;i<count;i++){
    let thisUnsafeHash = generateUnsafeHash();
    uhs += `${thisUnsafeHash}\n`;
    uniqueCheck.push(thisUnsafeHash)
  }

  if(_.uniq(uniqueCheck).length !== uniqueCheck.length){
    console.error('Duplicated ids detected. Try running command again.');
  }
  else{
    console.log(uhs);
  }
};

export function generateSafePassword(){
  const password = passwordGenerator(17, false);
  return password;
}


export function omitDeleted(object){
  return _.omitBy(object, item => {
    return item._deleted;
  });
}

export function addKeysAsIds(objects){
  return _.mapValues(objects, (object, key) => {
    return {
      _id: key,
      ...object
    }
  });
}

export function logAndDispatchErrorForAction(actionName, err, dispatchFunction, dispatchType, callback){
  console.error('Error during', actionName, ':', err);
  dispatchFunction({
    type: dispatchType,
    payload: err.message
  });

  callIfFunction(callback);
}

export function unwrapDoc(doc){
  //don't add onto doc if not object
  if(typeof doc.data() !== 'object'){
    return doc.data();
  }

  let unwrappedDoc = {
    ...doc.data()
  };

  unwrappedDoc._id = doc.id;

  return unwrappedDoc;
}

export function unwrapDocs(docs, includeDeleted = false){
  let unwrappedDocs = [];

  docs.forEach(doc => {

    //don't add onto unwrappedDocs if not object
    if(typeof doc.data() !== 'object'){
      return;
    }

    let unwrappedDoc = doc.data();
    unwrappedDoc._id = doc.id;

    if(!unwrappedDoc._deleted || includeDeleted){
      unwrappedDocs.push(unwrappedDoc);
    }
  });

  return unwrappedDocs;
}

export function withoutId(object){
  let copy = { ...object };
  delete copy._id;
  return copy;
}
export function withoutIds(objects){
  const copies = _.map(objects, object => {
    return withoutId(object);
  });

  return copies;
}

//initialize sentry for error logging
if(CONFIG.SENTRY_CONFIG){
  Sentry.init({
    dsn: CONFIG.SENTRY_CONFIG.DSN,
    maxBreadcrumbs: 0,
    //debug: true,
    //uncomment below to request report from user when error happens
    // beforeSend: event => {
    //   //https://docs.sentry.io/learn/user-feedback/?platform=browser
    //   event.exception && Sentry.showReportDialog();
    //   return event;
    // }
  });
}

export function logError(err){
  console.log('Logging error to Sentry: ', err);
  Sentry.captureException(err);
  console.log('error logged!');
}

export function scopeErrorReportingToUser(user){
  Sentry.configureScope(scope => {
    scope.setUser(user);
  });
}

export function scopeFullstoryCaptureToUser(user){
  if(!isProduction()){
    //only tracking in production...
    return;
  }
  FullStoryAPI('identify', user.uid, {
    displayName: user.firstName + ' ' + user.lastName,
    email: user.email,
  });

  // if(typeof FS !== 'undefined'){
  //   FS.identify(user.uid, {
      // displayName: user.firstName + ' ' + user.lastName,
      // email: user.email,
  //     // TODO: Add your own custom user variables here, details at
  //     // http://help.fullstory.com/develop-js/setuservars
  //     // reviewsWritten_int: 14,
  //   });
  // }
}

export function unscopeFullstoryCapture(){
  if(!isProduction()){
    return;
  }
  FullStoryAPI('identify', false);
}

export const Firebase = firebase.initializeApp(CONFIG.FIREBASE_CONFIG);

if(!isProduction() && CONFIG.ADVANCED_DEBUG){
  firebase.firestore.setLogLevel('debug');
}

export const FirebaseForUserCreation = firebase.initializeApp(CONFIG.FIREBASE_CONFIG, "usercreation");

//Allow access to debugging functions if on localhost
if(isLocalhost()){
  //Allow access to firebase globally
  window.firebase = Firebase;

  //Allow direct access to firestore globally
  window.firestore = firebase.firestore();

  //allow direct access to moment
  window.moment = moment;

  //allow
  window.generateSafePassword = generateSafePassword;
}

//if LOCAL app config, setup emulator to use local functions
if(APP_CONFIG.IS_LOCAL){
  Firebase.functions().useFunctionsEmulator('http://localhost:5000');
  //Firebase.functions().useFunctionsEmulator('http://localhost:5001');
}
else{
  //not locl
}

//unregister any previously registered service workers - which might have been registred by createreactapp then unused
if ('serviceWorker' in navigator) {

    navigator.serviceWorker.getRegistrations().then(function(registrations) {

    for(let registration of registrations) {

            registration.unregister()

    }}).catch(function(err) {

        console.log('Service Worker registration failed: ', err);

    });
}

export const FirebasePerformance = firebase.performance();

const firestore = firebase.firestore();



// WEBPACK FOOTER //
// ./src/util/index.js